import { authApiClient } from '~/lib/api-client'

export type LiveOddsAvailabilityReason
  = | 'fresh'
    | 'stale'
    | 'provider_market_not_open'
    | 'missing_equivalent'
    | 'provider_failed'
    | 'rate_limited'
    | 'invalid_payload'
    | 'unsupported_market'
    | 'skipped_by_budget'
    | 'polymarket_price_unavailable'

export interface AdminMonitoringScopeFilter {
  competitionCode?: string
  season?: number
}

export interface AdminMonitoringListFilter extends AdminMonitoringScopeFilter {
  limit?: number
}

export interface AdminMonitoringSummary {
  generatedAt: string
  provider: {
    state: 'disabled' | 'healthy' | 'degraded'
    configured: boolean
    failureCount: number
  }
  quota: {
    accountLimit: number
    retainedReserve: number
    requestsUsedToday: number
    requestsAvailableBeforeReserve: number
  }
  cadence: {
    targetMinMinutes: number
    targetMaxMinutes: number
    state: 'never_run' | 'on_schedule' | 'late'
    lastCompletedAt: string | null
  }
  freshness: {
    freshTargetCount: number
    unavailableTargetCount: number
    unavailableByReason: Array<{ reason: LiveOddsAvailabilityReason, count: number }>
  }
  projection: {
    state: 'not_started' | 'local_replay' | 'shadow' | 'backfill' | 'public'
    latestBatchId: string | null
    latestCommittedAt: string | null
    projectedCount: number
    collisionCount: number
    publicReadEnabled: boolean
  }
  runtime: {
    environment: string
    state: 'idle' | 'running' | 'degraded'
    latestRefreshRunId: string | null
  }
  fixtureCoverage: {
    totalFixtureCount: number
    mappedFixtureCount: number
    unmappedFixtureCount: number
  }
  targetCoverage: {
    reviewedTargetCount: number
    refreshReadyTargetCount: number
  }
  latestRefreshRun: {
    refreshRunId: string
    trigger: 'manual' | 'scheduled' | 'retry'
    status: 'completed' | 'partial' | 'failed' | 'invalidated' | 'interrupted'
    mode: 'preview' | 'dry_run' | 'write'
    completedAt: string | null
    requestCount: number
  } | null
}

export interface AdminMonitoringFixtureRow {
  apiFootballFixtureId: number
  fixtureId: string
  competitionName: string
  kickoffAt: string
  homeTeamName: string
  awayTeamName: string
  mappedTargetCount: number
  latestAvailabilityReason: LiveOddsAvailabilityReason | null
  latestCheckedAt: string | null
  reviewLinks: Array<{ label: string, href: string }>
}

export interface AdminMonitoringUnavailableRow {
  reason: LiveOddsAvailabilityReason
  apiFootballFixtureId: number
  fixtureLabel: string
  mappingId: string
  outcomeLabel: string
  marketSection: string
  lastAttemptedAt: string | null
  nextAction:
    | 'wait_for_provider_market'
    | 'review_mapping'
    | 'refresh_later'
    | 'check_provider_health'
    | 'inspect_payload'
    | 'disable_or_remap_target'
    | 'check_polymarket_price'
  reviewLinks: Array<{ label: string, href: string }>
}

const monitoringClient = authApiClient.api.admin.monitoring

async function parseJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let message = `Request failed with status ${response.status}`
    try {
      const body = await response.json() as {
        message?: string
        error?: { message?: string, issues?: Array<{ message?: string }> }
      }
      message = body.message ?? body.error?.message ?? body.error?.issues?.[0]?.message ?? message
    }
    catch {
      // Keep the status fallback for non-JSON errors.
    }
    throw new Error(message)
  }
  return response.json() as Promise<T>
}

export async function getAdminMonitoringSummary(filter: AdminMonitoringScopeFilter = {}) {
  const response = await monitoringClient.summary.$get({
    query: filter,
    header: { Authorization: '' },
  })
  return parseJson<AdminMonitoringSummary>(response)
}

export async function listAdminMonitoringFixtures(filter: AdminMonitoringListFilter = { limit: 50 }) {
  const response = await monitoringClient.fixtures.$get({
    query: filter,
    header: { Authorization: '' },
  })
  return parseJson<AdminMonitoringFixtureRow[]>(response)
}

export async function listAdminMonitoringUnavailable(filter: AdminMonitoringListFilter = { limit: 50 }) {
  const response = await monitoringClient.unavailable.$get({
    query: filter,
    header: { Authorization: '' },
  })
  return parseJson<AdminMonitoringUnavailableRow[]>(response)
}
